import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  CreateTaskListPayload,
  UpdateTaskListPayload,
  TaskListType,
  GetTaskListsParams,
  GetTaskListsResponse,
  GetTaskListDetailResponse,
  GetTaskDetailResponse,
  TaskListDetail,
  CreateTaskApiPayload,
  TaskEntity,
} from '@/types/task-lists';

export async function getTaskLists(
  companyId: string,
  type: TaskListType,
  params?: GetTaskListsParams
) {
  const cleanParams = params
    ? Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== '' && v !== undefined)
      )
    : undefined;
  const { data } = await api.get<GetTaskListsResponse>(
    `/companies/${companyId}/${type}/task-lists`,
    { params: cleanParams }
  );
  return data;
}

export async function getTaskListById(
  companyId: string,
  type: TaskListType,
  id: string
) {
  const { data } = await api.get<GetTaskListDetailResponse>(
    `/companies/${companyId}/${type}/task-lists/${id}`
  );
  return data;
}

export async function createTaskList(
  companyId: string,
  payload: CreateTaskListPayload
) {
  const { data } = await api.post<ApiResponse<TaskListDetail>>(
    `/companies/${companyId}/${payload.type}/task-lists`,
    {
      name: payload.name,
      description: payload.description,
      isActive: payload.isActive,
    }
  );
  return data;
}

export async function updateTaskList(
  companyId: string,
  id: string,
  type: TaskListType,
  payload: UpdateTaskListPayload
) {
  const { data } = await api.put<ApiResponse<TaskListDetail>>(
    `/companies/${companyId}/${type}/task-lists/${id}`,
    payload
  );
  return data;
}

export async function getTask(
  companyId: string,
  type: TaskListType,
  taskListId: string,
  taskId: string
) {
  const { data } = await api.get<GetTaskDetailResponse>(
    `/companies/${companyId}/${type}/task-lists/${taskListId}/tasks/${taskId}`
  );
  return data;
}

export async function createTask(
  companyId: string,
  taskListId: string,
  type: TaskListType,
  payload: CreateTaskApiPayload
) {
  const { data } = await api.post<ApiResponse<TaskEntity>>(
    `/companies/${companyId}/${type}/task-lists/${taskListId}/tasks`,
    payload
  );
  return data;
}

export async function updateTask(
  companyId: string,
  taskListId: string,
  taskId: string,
  type: TaskListType,
  payload: CreateTaskApiPayload
) {
  const { data } = await api.put<ApiResponse<TaskEntity>>(
    `/companies/${companyId}/${type}/task-lists/${taskListId}/tasks/${taskId}`,
    payload
  );
  return data;
}

export async function deleteTaskList(
  companyId: string,
  type: TaskListType,
  id: string
) {
  await api.delete(`/companies/${companyId}/${type}/task-lists/${id}`);
}

export async function deleteTask(
  companyId: string,
  type: TaskListType,
  taskListId: string,
  taskId: string
) {
  await api.delete(
    `/companies/${companyId}/${type}/task-lists/${taskListId}/tasks/${taskId}`
  );
}

export async function reorderTask(
  companyId: string,
  type: TaskListType,
  taskListId: string,
  taskId: string,
  payload: { orderIndex: number }
) {
  const { data } = await api.patch<ApiResponse<void>>(
    `/companies/${companyId}/${type}/task-lists/${taskListId}/tasks/${taskId}/order`,
    payload
  );
  return data;
}

/**
 * Every task of the list, nested by dependency, for the prerequisite picker:
 * a step may wait on any other step (chains of steps, D68).
 */
export async function getParentTasks(
  companyId: string,
  type: TaskListType,
  taskListId: string
) {
  const { data } = await api.get<GetTaskListDetailResponse>(
    `/companies/${companyId}/${type}/task-lists/${taskListId}`
  );
  return data;
}
